// src/components/ui/Tables/CopyableCell.tsx
import React, { FC, useState, useEffect, useCallback, useRef } from "react";
import { CellProps } from "react-table";
import SupplyPortal from "../../../../SupplyPortal";
import CopyToClipboardIconWrapper from "../../../../CopyToClipboardIconWrapper";
import BaseToolTip from "../../../../BaseToolTip";

interface CopyableCellProps extends CellProps<any> {
	originalCell: (props: CellProps<any>) => React.ReactNode;
}

const CopyableCell: FC<CopyableCellProps> = ({
	originalCell,
	value,
	...cellProps
}) => {
	const [isCopied, setIsCopied] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [position, setPosition] = useState({ left: 0, top: 0 });
	const cellRef = useRef<HTMLDivElement>(null);
	const hideTimer = useRef<number>();

	// Helper to clear any pending hide
	const clearHide = () => {
		if (hideTimer.current) window.clearTimeout(hideTimer.current);
	};

	// Show icon, record coords
	const showIcon = () => {
		clearHide();
		if (!cellRef.current) return;
		const rect = cellRef.current.getBoundingClientRect();
		setPosition({ left: rect.right + 4, top: rect.top + rect.height / 2 });
		setHovered(true);
	};

	// Hide icon after 100ms (gives time to land on the icon)
	const hideIcon = () => {
		clearHide();
		hideTimer.current = window.setTimeout(() => setHovered(false), 100);
	};

	const handleCopy = useCallback((text: string) => {
		setIsCopied(true);
	}, []);

	useEffect(() => {
		if (!isCopied) return;
		const t = window.setTimeout(() => setIsCopied(false), 2000);
		return () => window.clearTimeout(t);
	}, [isCopied]);

	const text = String(value);
	const canCopy = value != null && value !== "—";

	return (
		<>
			{/* 1) Cell container */}
			<div
				ref={cellRef}
				className="relative flex items-center w-full"
				onMouseEnter={showIcon}
				onMouseMove={showIcon}
				onMouseLeave={hideIcon}
			>
				{originalCell({ value, ...cellProps } as CellProps<any>)}
			</div>

			{/* {canCopy && (

			{/* 2) Portaled icon */}
			{canCopy && hovered && (
				<SupplyPortal containerId="copyable-cell-portal">
					<div
						style={{
							position: "fixed",
							left: position.left,
							top: position.top,
							transform: "translateY(-50%)",
							zIndex: 9999,
							pointerEvents: "auto",
						}}
						onMouseEnter={showIcon}
						onMouseLeave={hideIcon}
					>
						<BaseToolTip
							message={isCopied ? "Copied" : "Copy"}
							toolTipPlacement="right"
						>
							<CopyToClipboardIconWrapper
								text={text}
								copyText={text}
								withText={false}
								lastCopiedText={isCopied ? text : ""}
								setLastCopiedText={handleCopy}
							/>
						</BaseToolTip>
					</div>
				</SupplyPortal>
			)}
		</>
	);
};

export default React.memo(CopyableCell);
